<template>
    <div class="success-page">
        <div class="container">
            <div class="success-content" ref="content">
                <div class="logo">
                     <img v-if="logoUrl" :src="logoUrl" alt="Logo"/>
                </div>

                <div class="success-icon">
                    <fa icon="fa-circle-check" class="check-icon"/>
                </div>

                <div class="success-message">
                    <h1>Pedido realizado com sucesso!</h1>
                    <!-- Message based on payment type and status -->
                    <p class="info-email" v-if="isPixWaitingPayment">
                        Realize o pagamento via PIX para liberar seu acesso ao conteúdo.
                    </p>
                    <p class="info-email" v-else-if="isBoletoGenerated">
                        Realize o pagamento do Boleto para liberar seu acesso ao conteúdo. O boleto também foi enviado para o seu e-mail.
                    </p>
                    <!-- Show approved message if not PIX waiting and not Boleto generated, assuming card payments are approved on this screen -->
                    <p class="info-email" v-else>
                        Seu pagamento foi aprovado! O acesso ao conteúdo já está liberado.
                    </p>
                </div>
                <div class="success-message">
                    <div class="next-steps">
                         <!-- Adjust email message based on payment status -->
                        <p class="info-email" v-if="isPaymentPending">
                            Você receberá um email após a aprovação do pagamento com um link para ativação da conta e criação da senha. Verifique sua caixa de entrada.
                        </p>
                         <p class="info-email" v-else>
                            Você receberá um email confirmando seu pedido e com um link para ativação da conta e criação da senha. Verifique sua caixa de entrada.
                        </p>
                    </div>
                </div>

                <div class="order-details" v-if="dataPaymentWeflix?.idOrder">
                    <div class="order-number">
                        <span>Pedido: #{{ dataPaymentWeflix.idOrder }}</span>
                    </div>
                    <div class="order-status">
                         <!-- Status based on payment type and status -->
                        <span :class="['status', isPaymentPending ? 'pending' : 'approved']">
                            {{ isPaymentPending ? 'Aguardando Pagamento' : 'Pagamento Aprovado' }}
                        </span>
                    </div>
                </div>

                <!-- Seção PIX - Only show if payment is PIX and waiting -->
                <div class="pix-details" v-if="isPixWaitingPayment && dataPaymentWeflix?.transaction">
                    <h3>Pagamento via PIX</h3>
                    <div class="pix-container">
                        <div class="qr-code" v-if="dataPaymentWeflix.transaction.qr_code_url">
                            <img :src="dataPaymentWeflix.transaction.qr_code_url" alt="QR Code PIX"/>
                        </div>
                        <div class="pix-instructions">
                            <h4>Como pagar com PIX:</h4>
                            <ol>
                                <li>Abra o app do seu banco</li>
                                <li>Escolha pagar via PIX</li>
                                <li>Escaneie o QR Code ao lado</li>
                                <li>Confirme as informações e pague</li>
                            </ol>
                            <!-- Use order.total if available, otherwise fallback might be needed -->
                            <p class="pix-amount">Valor a pagar: <strong>R$ {{ formattedPrice(dataPaymentWeflix.order?.total) }}</strong></p>
                        </div>
                    </div>
                    <div class="pix-copy" v-if="dataPaymentWeflix.transaction.qr_code">
                        <p>Ou copie o código PIX abaixo:</p>
                        <div class="copy-input">
                            <input type="text" readonly :value="dataPaymentWeflix.transaction.qr_code"/>
                            <button @click="copyPixKey" class="btn-copy">
                                <fa icon="fa-copy"/> Copiar
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Seção Boleto - Apenas se for boleto e estiver gerado -->
                <div class="boleto-details" v-if="isBoletoGenerated && dataPaymentWeflix?.transaction">
                    <h3>Pagamento via Boleto</h3>
                    <div class="boleto-instructions">
                        <p>O boleto para pagamento foi gerado. Você pode visualizá-lo ou imprimi-lo clicando no botão abaixo. Ele também foi enviado para o seu e-mail.</p>
                        <p>Lembre-se de pagar até a data de vencimento para liberar seu acesso.</p>
                        <p class="boleto-amount" v-if="dataPaymentWeflix.order?.total">Valor a pagar: <strong>R$ {{ formattedPrice(dataPaymentWeflix.order?.total) }}</strong></p>
                        <a :href="dataPaymentWeflix.transaction.pdf" target="_blank" class="btn-view-boleto" v-if="dataPaymentWeflix.transaction.pdf">
                            <fa icon="fa-file-pdf" /> Visualizar / Imprimir Boleto
                        </a>
                        <p v-if="!dataPaymentWeflix.transaction.pdf">
                            O link para o boleto não está disponível no momento. Por favor, verifique seu e-mail ou entre em contato com o suporte.
                        </p>
                        <!-- Exemplo de como exibir linha digitável se disponível no dataPaymentWeflix.transaction -->
                        <!--
                        <div class="boleto-code-line" v-if="dataPaymentWeflix.transaction.digitable_line">
                            <p>Ou copie a linha digitável do boleto abaixo:</p>
                            <div class="copy-input">
                                <input type="text" readonly :value="dataPaymentWeflix.transaction.digitable_line"/>
                                <button @click="copyToClipboard(dataPaymentWeflix.transaction.digitable_line, 'Linha digitável')" class="btn-copy">
                                    <fa icon="fa-copy"/> Copiar Linha Digitável
                                </button>
                            </div>
                        </div>
                        -->
                    </div>
                </div>

                <!-- Resumo da Compra -->
                <div class="purchase-details" v-if="dataPaymentWeflix">
                    <h3>Resumo da Compra</h3>
                    <div class="purchase-info">
                        <div class="purchase-item">
                            <!-- Use collectionCheckout for collection details -->
                            <p><strong>Conteúdo:</strong> {{ collectionCheckout?.comercialName || dataPaymentWeflix.order?.nameWebsite || 'Não disponível' }}</p>
                            <!-- Use order.total from payment data if available, otherwise fallback to collection price -->
                            <p><strong>Valor:</strong> R$ {{ formattedPrice(dataPaymentWeflix?.order?.total ?? collectionCheckout?.price) }}</p>
                            <!-- Use dayExpired directly from collectionCheckout -->
                            <p><strong>Validade:</strong> {{ collectionCheckout?.dayExpired || 'N/A' }} dias</p>
                            <!-- Payment method comes from payment data -->
                            <p><strong>Forma de Pagamento:</strong> {{ getPaymentMethod(dataPaymentWeflix) }}</p>
                            <!-- Purchase date comes from payment data (order) -->
                            <p><strong>Data da Compra:</strong> {{ formattedDate(dataPaymentWeflix?.order?.dateInsert) }}</p>
                        </div>
                    </div>
                </div>
                <!-- Removed redundant purchase-details block -->

                <div class="action-buttons">
                    <button @click="redirect" class="btn-primary">
                        <fa style="color: white;" icon="fa-right-to-bracket" /> Voltar para o site
                    </button>
                    <button @click="exportarPDF" class="btn-export">
                        <fa style="color: white;" icon="fa-file-pdf" /> Exportar Comprovante
                    </button>
                </div>
            </div>
        </div>


        <footer class="footer">
            <div class="footer-content">
                <div class="logo-footer">
                    <img src="../../assets/Weflix/logo_weflix-black.png" alt="Weflix Logo"/>
                </div>
                <p class="copyright">© 2024 Weflix - Todos os direitos reservados</p>
            </div>
        </footer>
    </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
// Ensure html2pdf is installed or handle its import appropriately
// import html2pdf from 'html2pdf.js'; // Example if using npm package
import html2pdf from 'html2pdf.js'; // Assuming it's installed

export default {
    name: 'SuccessPage',
    data() {
        return {
            // No local state needed based on current logic
        }
    },
    computed: {
        ...mapState(['finalWebsite','dataPaymentWeflix', 'collectionCheckout']),
        /**
         * Checks if the current payment is PIX and waiting for confirmation.
         * @returns {boolean}
         */
        isPixWaitingPayment() {
            return this.dataPaymentWeflix?.transaction?.transaction_type === 'pix' &&
                   this.dataPaymentWeflix?.transaction?.status === 'waiting_payment';
        },
        /**
         * Checks if the current payment is Boleto and has been generated.
         * @returns {boolean}
         */
        isBoletoGenerated() {
            return this.dataPaymentWeflix?.transaction?.transaction_type === 'boleto' &&
                   this.dataPaymentWeflix?.transaction?.status === 'generated';
        },
        /**
         * Checks if there is any payment pending (PIX or Boleto).
         * @returns {boolean}
         */
        isPaymentPending() {
            return this.isPixWaitingPayment || this.isBoletoGenerated;
        },
        /**
         * Gets the logo URL from the payment data or fallback from Vuex store.
         * @returns {string|null}
         */
        logoUrl() {
            return this.dataPaymentWeflix?.listWebsite?.[0]?.logoFinal || this.finalWebsite?.logoFinal || null;
        }
    },
    methods: {
        ...mapActions(['logout','storeDataPaymentWeflix','storeCollectionCheckout']),
        redirect() {
            // Clear sensitive data before redirecting
            this.storeDataPaymentWeflix({ dataPaymentWeflix: null });
            this.storeCollectionCheckout({ collectionCheckout: null });
            this.$router.push('/');
        },
        copyToClipboard(text, messagePrefix = 'Texto') {
            if (text) {
                navigator.clipboard.writeText(text)
                    .then(() => {
                        alert(`${messagePrefix} copiado para a área de transferência!`);
                    })
                    .catch(err => {
                        console.error(`Erro ao copiar ${messagePrefix.toLowerCase()}:`, err);
                        alert(`Erro ao copiar ${messagePrefix.toLowerCase()}.`);
                    });
            }
        },
        copyPixKey() {
            // Check if transaction and qr_code exist before copying
            if (this.dataPaymentWeflix?.transaction?.qr_code) {
                this.copyToClipboard(this.dataPaymentWeflix.transaction.qr_code, 'Chave PIX');
            }
        },
        exportarPDF() {
            // Ensure html2pdf is loaded correctly
            if (typeof html2pdf === 'undefined') {
                 console.error("html2pdf.js is not loaded.");
                 alert("Erro ao gerar PDF: Biblioteca não encontrada.");
                 return;
            }
            const element = this.$refs.content;
            const orderId = this.dataPaymentWeflix?.idOrder || 'sem-numero';
            const opt = {
                margin: [10, 10, 10, 10], // top, left, bottom, right
                filename: `pedido-${orderId}.pdf`,
                image: { type: 'jpeg', quality: 0.98 }, // Use jpeg for potentially smaller size
                html2canvas: {
                    scale: 2, // Improves quality
                    useCORS: true, // Important if images are from other domains
                    logging: false // Disable verbose logging
                },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            };
            // Use promise based approach for better feedback/error handling
            html2pdf().from(element).set(opt).save()
                .catch(err => {
                    console.error("Erro ao gerar PDF:", err);
                    alert("Ocorreu um erro ao gerar o comprovante em PDF.");
                });
        },
        /**
         * Formats a numeric value into Brazilian currency format (R$ X,XX).
         * Handles null or undefined values.
         * @param {number|string|null|undefined} value - The numeric value to format.
         * @returns {string} - The formatted price string or '0,00'.
         */
        formattedPrice(value) {
            const numberValue = Number(value); // Convert to number just in case
            // Check if value is a valid number
            if (isNaN(numberValue)) {
                return '0,00'; // Return default value if input is not a valid number
            }
            // Format the number to two decimal places and replace dot with comma
            return numberValue.toFixed(2).replace('.', ',');
        },
        /**
         * Formats a date string into Brazilian date format (DD/MM/YYYY).
         * Handles null, undefined, or invalid date strings.
         * @param {string|null|undefined} dateString - The date string to format.
         * @returns {string} - The formatted date string or 'Data indisponível'.
         */
        formattedDate(dateString) {
            if (!dateString) return 'Data indisponível';
            try {
                // Handles ISO 8601 format like "2025-05-02T19:10:05.134180410Z"
                const date = new Date(dateString);
                // Check if date is valid
                if (isNaN(date.getTime())) {
                     console.warn("Invalid date string received:", dateString);
                    return 'Data inválida';
                }
                return date.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
            } catch (e) {
                console.error("Error formatting date:", dateString, e);
                return 'Data inválida';
            }
        },
        /**
         * Determines the payment method based on available data.
         * Handles cases where transaction or order might be null.
         * @param {object|null|undefined} paymentData - The payment data object.
         * @returns {string} - The name of the payment method.
         */
        getPaymentMethod(paymentData) {
            if (!paymentData) {
                return 'Não especificado';
            }

            // Check based on transaction_type first, as it's more specific
            if (paymentData.transaction?.transaction_type === 'pix') {
                return 'PIX';
            }
            if (paymentData.transaction?.transaction_type === 'boleto') {
                return 'Boleto Bancário';
            }

            // Check based on order.typePayment if transaction_type is not available or to confirm
            if (paymentData.order?.typePayment === 3) { // Boleto
                return 'Boleto Bancário';
            }
            if (paymentData.order?.typePayment === 1) { // Credit Card
                return 'Cartão de Crédito';
            }
            
            // Fallback for Credit Card if idOrder exists and no transaction object was present
            // (assuming PIX/Boleto would always have a transaction object from this flow)
            if (paymentData.idOrder && !paymentData.transaction) {
                return 'Cartão de Crédito';
            }

            return 'Não especificado';
        }
    },
    mounted() {
        // Set favicon using data from Vuex store or API data
        const faviconUrl = this.finalWebsite?.faviconFinal || this.dataPaymentWeflix?.listWebsite?.[0]?.faviconFinal;
        if (faviconUrl) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.getElementsByTagName('head')[0].appendChild(link);
            }
            // Determine type based on extension or assume common types
            let faviconType = 'image/x-icon';
            if (faviconUrl.endsWith('.png')) {
                faviconType = 'image/png';
            } else if (faviconUrl.endsWith('.svg')) {
                faviconType = 'image/svg+xml';
            }
            link.type = faviconType;
            link.href = faviconUrl;
        }
         // Set page title
        const websiteName = this.finalWebsite?.nome || this.dataPaymentWeflix?.listWebsite?.[0]?.nome || 'Weflix';
        document.title = `Pedido Confirmado - ${websiteName}`;
    },
    created() {
        // Redirect if essential payment data (like idOrder) is missing
        // This check remains important.
        if (!this.dataPaymentWeflix || !this.dataPaymentWeflix.idOrder) {
            console.warn('Dados de pagamento essenciais ausentes (idOrder), redirecionando para home.');
            this.$router.push('/');
        }
    },
    // Optional: Add beforeDestroy or unmounted hook to clear data if component is left
    // beforeDestroy() {
    //     this.storeDataPaymentWeflix({ dataPaymentWeflix: null });
    //     this.storeCollectionCheckout({ collectionCheckout: null });
    // }
}
</script>

<style scoped>
.success-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 100%);
    display: flex;
    flex-direction: column;
    font-family: 'Poppins', sans-serif;
}

.container {
    width: 100%;
    max-width: 1140px; /* Consistent max-width */
    padding-right: 15px;
    padding-left: 15px;
    margin-right: auto;
    margin-left: auto;
}

.success-content {
    max-width: 800px;
    margin: 40px auto;
    padding: 40px;
    background: white;
    border-radius: 20px;
    box-shadow: 0 15px 35px rgba(0,0,0,0.1);
    transition: all 0.3s ease;
}

.success-content:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
}

.logo {
    text-align: center;
    min-height: 50px; /* Ensure space even if logo fails to load */
}

.logo img {
    max-width: 220px;
    max-height: 80px; /* Prevent overly large logos */
    object-fit: contain; /* Ensure logo aspect ratio is maintained */
    margin-bottom: 30px;
    transition: transform 0.3s ease;
}

.logo img:hover {
    transform: scale(1.05);
}

.success-icon {
    margin: 30px 0;
    text-align: center;
}

.success-icon .check-icon {
    font-size: 90px;
    color: #4CAF50;
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
}

.success-message {
    text-align: center;
    margin-bottom: 40px;
}

.success-message h1 {
    color: #2C3E50;
    font-size: 36px;
    font-weight: 700;
    margin-bottom: 20px;
    letter-spacing: -0.5px;
}

.info-email {
    color: #666;
    font-size: 18px;
    line-height: 1.6;
}

.order-details {
    background: #f8f9fa;
    padding: 25px;
    border-radius: 15px;
    margin: 30px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
    flex-wrap: wrap; /* Allow wrapping on smaller screens */
    gap: 15px; /* Add gap for wrapped items */
}
.order-number span, .order-status span {
    font-size: 16px;
    color: #333;
}

.status {
    padding: 10px 20px;
    border-radius: 25px;
    font-size: 14px;
    font-weight: 600;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status.pending {
    background: linear-gradient(45deg, #FFC107, #FF9800);
}

.status.approved {
    background: linear-gradient(45deg, #4CAF50, #45a049);
}

.pix-details, .boleto-details { /* Added .boleto-details */
    background: #f8f9fa;
    padding: 35px;
    border-radius: 15px;
    margin: 30px 0;
    text-align: left;
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
}

.pix-details h3, .boleto-details h3 { /* Added .boleto-details */
    color: #2C3E50;
    font-size: 24px;
    margin-bottom: 25px;
    text-align: center;
}

.pix-container {
    display: flex;
    gap: 30px;
    align-items: flex-start;
    margin-bottom: 30px;
}

.qr-code {
    background: white;
    padding: 15px;
    border: 1px solid #eee; /* Subtle border */
    border-radius: 10px;
    width: 200px; /* Slightly smaller */
    flex-shrink: 0;
    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
}

.qr-code img {
    width: 100%;
    height: auto;
    display: block; /* Remove extra space below image */
    border-radius: 4px; /* Smaller radius */
}

.pix-instructions {
    flex-grow: 1;
}

.pix-instructions h4 {
    color: #2C3E50;
    margin-bottom: 15px;
    font-size: 18px;
}

.pix-instructions ol {
    padding-left: 20px;
    margin-bottom: 20px;
    list-style: decimal; /* Ensure numbers show */
}

.pix-instructions li {
    margin-bottom: 10px;
    color: #666;
    font-size: 15px; /* Slightly smaller text */
    line-height: 1.5;
}

.pix-amount, .boleto-amount { /* Added .boleto-amount */
    font-size: 18px;
    color: #2C3E50;
    margin-top: 20px;
}
.pix-amount strong, .boleto-amount strong { /* Added .boleto-amount */
    font-weight: 700;
}

.pix-copy {
    text-align: center;
    margin-top: 20px;
}
.pix-copy p {
    margin-bottom: 10px;
    color: #555;
}

.copy-input {
    display: flex;
    gap: 12px;
    justify-content: center;
    align-items: center; /* Align items vertically */
    margin-top: 15px;
}

.copy-input input {
    padding: 12px 15px; /* Adjusted padding */
    border: 1px solid #ccc; /* Standard border */
    border-radius: 8px;
    width: 100%; /* Take available width */
    max-width: 400px; /* Limit max width */
    font-size: 14px; /* Smaller font for code */
    background-color: #f9f9f9; /* Light background */
    color: #333;
    transition: all 0.3s ease;
    box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
    overflow-x: auto; /* Handle long codes */
}

.copy-input input:focus {
    border-color: #2196F3;
    outline: none;
    box-shadow: 0 0 5px rgba(33, 150, 243, 0.5); /* Focus indicator */
}

.btn-copy {
    background: linear-gradient(45deg, #2196F3, #1976D2);
    color: white;
    border: none;
    padding: 12px 25px; /* Adjusted padding */
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
    display: inline-flex; /* Align icon and text */
    align-items: center;
    gap: 8px; /* Space between icon and text */
    flex-shrink: 0; /* Prevent shrinking */
}

.btn-copy:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(33,150,243,0.3);
}
.btn-copy:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(33,150,243,0.2);
}

/* Boleto Specific Styles */
.boleto-instructions {
    text-align: center;
}

.boleto-instructions p {
    color: #666;
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 15px;
}

.boleto-amount {
    margin-bottom: 25px !important; /* Ensure spacing before button */
}

.btn-view-boleto {
    background: linear-gradient(45deg, #007bff, #0056b3); /* Blue gradient for boleto */
    color: white;
    border: none;
    padding: 12px 25px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none; /* For <a> tag */
    margin-top: 10px; /* Spacing from text */
}

.btn-view-boleto:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 123, 255, 0.3);
}
.btn-view-boleto:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

.boleto-code-line { /* Styles for boleto code line if implemented */
    margin-top: 20px;
    text-align: center;
}
.boleto-code-line p {
    margin-bottom: 10px;
    color: #555;
}


.purchase-details {
    background: white;
    padding: 35px;
    border-radius: 15px;
    margin: 30px 0;
    border: 1px solid #e8e8e8; /* Lighter border */
    box-shadow: 0 5px 15px rgba(0,0,0,0.05);
}

.purchase-details h3 {
    color: #2C3E50;
    font-size: 24px;
    margin-bottom: 25px;
    text-align: center;
    font-weight: 600;
}

.purchase-info {
    display: grid;
    gap: 15px; /* Slightly reduced gap */
}

.purchase-item p {
    margin: 8px 0; /* Reduced margin */
    color: #555; /* Slightly darker text */
    font-size: 16px;
    line-height: 1.6;
    display: flex; /* Align label and value */
    justify-content: space-between;
    flex-wrap: wrap;
}

.purchase-item strong {
    color: #2C3E50;
    margin-right: 8px; /* Space after label */
}

.action-buttons {
    display: flex;
    gap: 20px;
    justify-content: center;
    margin-top: 40px;
    flex-wrap: wrap; /* Allow buttons to wrap */
}

.btn-primary, .btn-export {
    padding: 14px 30px; /* Adjusted padding */
    border-radius: 30px;
    border: none;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex; /* Use inline-flex */
    align-items: center;
    justify-content: center; /* Center content */
    gap: 10px;
    transition: all 0.3s ease;
    text-align: center; /* Ensure text is centered */
}

.btn-primary {
    background: linear-gradient(45deg, #2196F3, #1976D2);
    color: white;
}

.btn-export {
    background: linear-gradient(45deg, #FF5722, #F4511E);
    color: white;
}

.btn-primary:hover, .btn-export:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0,0,0,0.15); /* Softer shadow */
}
.btn-primary:active, .btn-export:active {
    transform: translateY(0);
    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
}

.footer {
    margin-top: auto; /* Pushes footer to bottom */
    padding: 30px 15px; /* Add horizontal padding */
    background: #f8f9fa; /* Light background for footer */
    text-align: center;
    border-top: 1px solid #e9ecef; /* Separator line */
}

.footer-content {
    max-width: 800px; /* Match content width */
    margin: 0 auto;
}

.logo-footer img {
    width: 120px; /* Slightly smaller footer logo */
    margin-bottom: 15px;
    opacity: 0.7; /* Slightly less opaque */
    transition: opacity 0.3s ease;
}

.logo-footer img:hover {
    opacity: 1;
}

.copyright {
    color: #6c757d; /* Standard muted color */
    font-size: 14px;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
    .success-content {
        margin: 20px 10px; /* Reduced margin */
        padding: 25px; /* Reduced padding */
    }

    .success-message h1 {
        font-size: 28px;
    }
    .info-email {
        font-size: 16px;
    }

    .order-details {
        flex-direction: column;
        align-items: stretch; /* Stretch items full width */
        text-align: center;
        padding: 20px 15px;
    }

    .pix-container { /* Also applies to boleto if structured similarly */
        flex-direction: column;
        align-items: center;
        gap: 20px;
    }
    .qr-code {
        width: 180px; /* Adjust QR code size */
    }

    .pix-instructions, .boleto-instructions { /* Added .boleto-instructions */
        text-align: center;
        width: 100%; /* Take full width */
    }
    .pix-instructions ol {
        padding-left: 0; /* Remove padding */
        list-style-position: inside; /* Keep numbers inside */
        text-align: left; /* Align list text left */
        max-width: 300px; /* Limit width for readability */
        margin-left: auto;
        margin-right: auto;
    }

    .copy-input {
        flex-direction: column;
        align-items: stretch; /* Stretch input/button */
    }
    .copy-input input {
        max-width: none; /* Remove max-width */
        text-align: center;
    }
    .btn-copy, .btn-view-boleto { /* Added .btn-view-boleto */
        width: 100%; /* Full width button */
        justify-content: center; /* Center icon/text */
        padding: 12px 15px;
    }

    .purchase-details {
        padding: 25px;
    }
    .purchase-item p {
        font-size: 15px;
        flex-direction: column; /* Stack label and value */
        align-items: flex-start; /* Align left */
        gap: 5px;
    }
     .purchase-item strong {
        width: 100%; /* Make label take full width */
    }

    .action-buttons {
        flex-direction: column;
        gap: 15px;
    }
    .btn-primary, .btn-export {
        width: 100%; /* Full width buttons */
        padding: 14px 20px;
    }

    .footer {
        padding: 20px 15px;
    }
}

@media (max-width: 480px) {
    .success-content {
        padding: 20px;
    }
    .success-message h1 {
        font-size: 24px;
    }
    .success-icon .check-icon {
        font-size: 70px;
    }
    .pix-details, .purchase-details, .boleto-details { /* Added .boleto-details */
        padding: 20px;
    }
    .pix-details h3, .purchase-details h3, .boleto-details h3 { /* Added .boleto-details */
        font-size: 20px;
    }
    .qr-code {
        width: 160px;
    }
    .pix-instructions h4, .boleto-instructions h4 { /* Added .boleto-instructions, assuming it might have h4 */
        font-size: 16px;
    }
     .pix-instructions li {
        font-size: 14px;
    }
    .pix-amount, .boleto-amount { /* Added .boleto-amount */
        font-size: 16px;
    }
    .copy-input input {
        font-size: 13px;
    }
    .btn-primary, .btn-export, .btn-copy, .btn-view-boleto { /* Added .btn-view-boleto */
        font-size: 15px;
    }
}


</style>